"use client";

// Aggregate-account constituent codes (issue #178).
//
// An aggregate account (e.g. MASLTD6.MT.X) rolls up several broker desk codes
// (MCxx). The backend returns, per aggregate account, the codes that actually
// traded in the current NIFTY cycle. We fetch that map ONCE here and expose it
// via context so `AccountBadge` can tooltip it wherever it renders — no need to
// thread the codes through every page's payload.
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

// account_id (string, JSON object key) -> live constituent codes.
const AggregateCodesCtx = createContext<Record<string, string[]>>({});

export function AggregateCodesProvider({ children }: { children: React.ReactNode }) {
  const [map, setMap] = useState<Record<string, string[]>>({});
  useEffect(() => {
    // Swallow failures (e.g. pre-auth 401) — the tooltip is purely additive,
    // so an empty map just means no badges get one.
    api.aggregateCodes().then(setMap).catch(() => setMap({}));
  }, []);
  return (
    <AggregateCodesCtx.Provider value={map}>{children}</AggregateCodesCtx.Provider>
  );
}

/** Live constituent codes for one account, or `[]` if it isn't an aggregate
 *  (or the provider isn't mounted). Safe to call unconditionally. */
export function useConstituentCodes(accountId: number | null | undefined): string[] {
  const map = useContext(AggregateCodesCtx);
  if (accountId == null) return [];
  return map[String(accountId)] ?? [];
}
